const resolveTrainerId = value => {
	if (!value) return null
	if (typeof value === 'object') {
		return value?._id || value?.id || null
	}
	return `${value}`.trim() || null
}

const resolveTrainerName = value => {
	if (!value) return ''
	if (typeof value === 'string') return value.trim()
	if (typeof value === 'object') {
		return `${value?.trainerName ?? value?.trenerName ?? value?.nazwa ?? value?.name ?? ''}`.trim()
	}
	return ''
}

const normalizeDistanceKm = value => {
	if (value === '' || value == null) return ''
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parsed : `${value}`.trim()
}

const normalizeHourlyRate = value => {
	if (value === '' || value == null) return ''
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parsed : `${value}`.trim()
}

const normalizeRatePerKm = value => {
	if (value === '' || value == null) return 1
	const parsed = Number(`${value}`.replace(',', '.'))
	return Number.isFinite(parsed) ? parsed : 1
}

export const normalizeRozliczono = value => (
	value === true || value === 1 || value === '1' || value === 'true'
)

export const DEFAULT_STAWKA_ZA_KILOMETR = 1

export const parseNumericInput = value => {
	if (value === '' || value == null) return null
	const parsed = Number(`${value}`.replace(',', '.'))
	return Number.isFinite(parsed) ? parsed : null
}

export const formatMoneyPln = value => {
	const parsed = parseNumericInput(value)
	if (parsed == null) return '—'
	return `${parsed.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} PLN`
}

export const calculateAssignedTrainerMileageTotal = row => {
	const distanceKm = parseNumericInput(row?.odleglosc_km)
	const ratePerKm = parseNumericInput(row?.stawka_za_kilometr) ?? DEFAULT_STAWKA_ZA_KILOMETR

	if (distanceKm == null) return null
	return distanceKm * ratePerKm
}

/** @deprecated Use calculateAssignedTrainerMileageTotal */
export const calculateAssignedTrainerTotal = calculateAssignedTrainerMileageTotal

const parseTimeToMinutes = value => {
	const parts = `${value || ''}`.trim().split(':')
	if (parts.length < 2) return null

	const hours = Number(parts[0])
	const minutes = Number(parts[1])
	if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null

	return hours * 60 + minutes
}

export const parseTrainingDurationHours = training => {
	const startMinutes = parseTimeToMinutes(training?.startTime || training?.godzina_rozpoczecia)
	const endMinutes = parseTimeToMinutes(training?.endTime || training?.godzina_zakonczenia)

	if (startMinutes == null || endMinutes == null) return null

	let diffMinutes = endMinutes - startMinutes
	if (diffMinutes < 0) diffMinutes += 24 * 60

	return diffMinutes / 60
}

export const resolveAssignedTrainerHours = (row, training) => {
	const rowHours = parseNumericInput(row?.ilosc_godzin)
	if (rowHours != null) return rowHours

	return parseTrainingDurationHours(training)
}

export const createTrainerExtraCost = () => ({
	_id: `trainer-cost-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	tytul: '',
	kwota_brutto: '',
	notatka: '',
})

export const normalizeTrainerExtraCosts = costs => (
	Array.isArray(costs) ? costs : []
).map(item => ({
	_id: item?._id || `trainer-cost-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	// Keep raw text while editing so spaces are not stripped mid-typing.
	tytul: `${item?.tytul ?? item?.title ?? item?.nazwa ?? ''}`,
	kwota_brutto: `${item?.kwota_brutto ?? item?.amountGross ?? item?.kwota ?? ''}`.trim(),
	notatka: `${item?.notatka ?? item?.note ?? item?.uwaga ?? item?.uwagi ?? ''}`,
}))

export const sumTrainerExtraCosts = costs => normalizeTrainerExtraCosts(costs).reduce((total, item) => {
	const amount = parseNumericInput(item.kwota_brutto)
	return total + (amount ?? 0)
}, 0)

export const serializeTrainerExtraCosts = costs => JSON.stringify(
	normalizeTrainerExtraCosts(costs).map(item => ({
		tytul: item.tytul,
		kwota_brutto: item.kwota_brutto,
		notatka: item.notatka,
	}))
)

export const calculateDoWyplatyDojazdy = row => calculateAssignedTrainerMileageTotal(row)

export const calculateDoWyplatyGodziny = (row, training) => {
	const hours = resolveAssignedTrainerHours(row, training)
	const hourlyRate = parseNumericInput(row?.stawka_godzinowa)

	if (hours == null || hourlyRate == null) return null
	return hours * hourlyRate
}

export const calculateDoWyplatyInne = row => {
	const costs = normalizeTrainerExtraCosts(row?.inne_koszty)
	if (!costs.length) return null
	return sumTrainerExtraCosts(row?.inne_koszty)
}

export const calculateDoWyplaty = (row, training) => {
	const mileageTotal = calculateDoWyplatyDojazdy(row) ?? 0
	const hourlyTotal = calculateDoWyplatyGodziny(row, training) ?? 0
	const extraCostsTotal = calculateDoWyplatyInne(row) ?? 0
	const hours = resolveAssignedTrainerHours(row, training) ?? 0

	const total = mileageTotal + hourlyTotal + extraCostsTotal
	if (!mileageTotal && !hourlyTotal && !extraCostsTotal && hours === 0) return null

	return total
}

export const applyRecalculatedDoWyplaty = (row, training) => {
	const calculated = calculateDoWyplaty(row, training)

	return {
		...row,
		do_wyplaty: calculated == null ? '' : calculated,
	}
}

export const normalizeAssignedTrainerItem = item => {
	if (!item || typeof item !== 'object') return null

	const trainerRelation = typeof item?.trainer === 'object' ? item.trainer : null
	const trainerId = resolveTrainerId(item?.trainerId ?? item?.trenerId ?? trainerRelation ?? item)
	const trainerName = resolveTrainerName(item?.trainerName ?? item?.trenerName ?? trainerRelation ?? item)

	if (!trainerId && !trainerName) return null

	return {
		trainerId: trainerId ? String(trainerId) : null,
		trainerName,
		miasto: `${item?.miasto ?? item?.trainerCity ?? item?.city ?? ''}`.trim(),
		odleglosc_km: normalizeDistanceKm(
			item?.odleglosc_km ?? item?.distanceKm ?? item?.distanceInKm ?? item?.distance ?? item?.odleglosc
		),
		stawka_godzinowa: normalizeHourlyRate(
			item?.stawka_godzinowa ?? item?.hourlyRateGross ?? item?.wynagrodzenie_godzinowe ?? item?.defaultHourlyRateGross
		),
		stawka_za_kilometr: normalizeRatePerKm(
			item?.stawka_za_kilometr ?? item?.ratePerKm ?? item?.stawkaZaKilometr
		),
		ilosc_godzin: normalizeHourlyRate(item?.ilosc_godzin ?? item?.hours ?? ''),
		do_wyplaty: normalizeHourlyRate(item?.do_wyplaty ?? ''),
		rozliczono: normalizeRozliczono(item?.rozliczono),
		inne_koszty: normalizeTrainerExtraCosts(
			item?.inne_koszty ?? item?.inneKoszty ?? item?.dodatkowe_koszty ?? item?.extraCosts
		),
	}
}

const migrateLegacyAssignedTrainers = training => {
	const rows = []
	const seen = new Set()

	const pushItem = item => {
		const normalized = normalizeAssignedTrainerItem(item)
		if (!normalized) return

		const dedupeKey = normalized.trainerId ? `id:${normalized.trainerId}` : `name:${normalized.trainerName}`
		if (seen.has(dedupeKey)) return
		seen.add(dedupeKey)
		rows.push(normalized)
	}

	const rawCompensation = training?.trainerCompensation
		?? training?.trenerCompensation
		?? training?.trenerzyCompensation
		?? []

	;(Array.isArray(rawCompensation) ? rawCompensation : []).forEach(pushItem)

	const rawTrenerzy = training?.trenerzy ?? training?.trainers ?? []
	;(Array.isArray(rawTrenerzy) ? rawTrenerzy : []).forEach(pushItem)

	const selectedTrainerIds = Array.isArray(training?.selectedTrainerIds) ? training.selectedTrainerIds : []
	const trainerNames = Array.isArray(training?.trainerNames) ? training.trainerNames : []

	selectedTrainerIds.forEach(trainerId => {
		pushItem({ trainerId })
	})

	trainerNames.forEach(trainerName => {
		pushItem({ trainerName })
	})

	return rows
}

export const normalizeAssignedTrainers = training => {
	if (Array.isArray(training?.assignedTrainers) && training.assignedTrainers.length) {
		return training.assignedTrainers
			.map(normalizeAssignedTrainerItem)
			.filter(Boolean)
	}

	return migrateLegacyAssignedTrainers(training)
}

/** @deprecated Use normalizeAssignedTrainers and read assignedTrainers instead. */
export const normalizeTrainingTrainers = training => {
	const assignedTrainers = normalizeAssignedTrainers(training)

	return {
		assignedTrainers,
		selectedTrainerIds: assignedTrainers.map(item => item.trainerId).filter(Boolean),
		trainerNames: assignedTrainers.map(item => item.trainerName).filter(Boolean),
		trainerCompensation: assignedTrainers.map(item => ({
			trainerId: item.trainerId,
			trainerName: item.trainerName,
			hourlyRateGross: `${item.stawka_godzinowa ?? ''}`,
			extraAmountGross: '',
		})),
	}
}

const getTrainerDistanceKm = trainer => {
	const distanceInKm = trainer?.distanceInKm ?? trainer?.distanceKm
	if (distanceInKm !== '' && distanceInKm != null) {
		const parsedKm = Number(distanceInKm)
		if (Number.isFinite(parsedKm)) return parsedKm
	}

	const distanceRaw = trainer?.distance ?? trainer?.odleglosc
	if (distanceRaw === '' || distanceRaw == null) return null

	const parsedDistance = Number(distanceRaw)
	if (!Number.isFinite(parsedDistance)) return null

	// API may return route length in meters when distanceInKm is absent.
	return parsedDistance > 1000 ? parsedDistance / 1000 : parsedDistance
}

export const formatTrainerDistanceKm = distanceKm => {
	if (distanceKm === '' || distanceKm == null) return null

	const parsedDistance = Number(distanceKm)
	if (!Number.isFinite(parsedDistance)) return null

	const formatted = parsedDistance.toFixed(1).replace(/\.0$/, '')
	return `${formatted} km`
}

export const toAssignedTrainerApiItem = (row, training) => {
	const doWyplaty = row?.do_wyplaty !== '' && row?.do_wyplaty != null
		? parseNumericInput(row.do_wyplaty)
		: calculateDoWyplaty(row, training)

	return {
		trainerId: row?.trainerId || '',
		trainerName: row?.trainerName || '',
		miasto: row?.miasto ?? '',
		odleglosc_km: row?.odleglosc_km ?? '',
		stawka_godzinowa: row?.stawka_godzinowa ?? '',
		stawka_za_kilometr: row?.stawka_za_kilometr ?? DEFAULT_STAWKA_ZA_KILOMETR,
		ilosc_godzin: row?.ilosc_godzin ?? '',
		do_wyplaty: doWyplaty == null ? '' : doWyplaty,
		rozliczono: normalizeRozliczono(row?.rozliczono),
		inne_koszty: normalizeTrainerExtraCosts(row?.inne_koszty).map(item => ({
			tytul: `${item.tytul || ''}`.trim(),
			kwota_brutto: item.kwota_brutto,
			notatka: `${item.notatka || ''}`.trim(),
		})),
	}
}

export const buildTrainerOptionRows = ({
	availableTrainers,
	certificationMap,
	getMissingCertificationNames,
	requiredCertificationIds,
}) => (Array.isArray(availableTrainers) ? availableTrainers : []).map(trainerItem => ({
	value: trainerItem._id || trainerItem.id,
	label: trainerItem.name || '—',
	trainerName: trainerItem.name || '—',
	miasto: trainerItem.city || `${trainerItem.miasto ?? ''}`.trim() || '',
	distanceKm: getTrainerDistanceKm(trainerItem),
	certificationNames: (Array.isArray(trainerItem.certificationIds) ? trainerItem.certificationIds : [])
		.map(certificationId => certificationMap.get(certificationId) || certificationId),
	defaultHourlyRateGross: trainerItem.defaultHourlyRateGross || `${trainerItem.stawka_godzinowa ?? ''}`.trim(),
	missingCertificationNames: getMissingCertificationNames(trainerItem, requiredCertificationIds),
}))

export const sortTrainerOptions = (trainerOptions, sortColumn, sortDirection) => {
	const direction = sortDirection === 'desc' ? -1 : 1

	return [...(Array.isArray(trainerOptions) ? trainerOptions : [])].sort((left, right) => {
		let leftValue
		let rightValue

		if (sortColumn === 'distanceKm') {
			leftValue = left.distanceKm ?? Number.MAX_SAFE_INTEGER
			rightValue = right.distanceKm ?? Number.MAX_SAFE_INTEGER
		} else if (sortColumn === 'defaultHourlyRateGross') {
			leftValue = Number(`${left.defaultHourlyRateGross || ''}`.replace(',', '.')) || 0
			rightValue = Number(`${right.defaultHourlyRateGross || ''}`.replace(',', '.')) || 0
		} else {
			leftValue = `${left[sortColumn] || ''}`.toLowerCase()
			rightValue = `${right[sortColumn] || ''}`.toLowerCase()
		}

		if (leftValue < rightValue) return -1 * direction
		if (leftValue > rightValue) return 1 * direction
		return 0
	})
}

export const buildAssignedTrainerRows = (training, trainers = []) => {
	const assignedTrainers = normalizeAssignedTrainers(training)
	const availableTrainers = Array.isArray(trainers) ? trainers : []

	return assignedTrainers.map(item => {
		const matchedTrainer = availableTrainers.find(candidate => {
			const candidateId = candidate?._id || candidate?.id
			const candidateName = `${candidate?.name ?? candidate?.nazwa ?? ''}`.trim()

			if (item.trainerId && candidateId && String(candidateId) === String(item.trainerId)) return true
			return !!(item.trainerName && candidateName && candidateName === item.trainerName)
		})

		const trainerId = item.trainerId || matchedTrainer?._id || matchedTrainer?.id || null
		const trainerName = item.trainerName || matchedTrainer?.name || matchedTrainer?.nazwa || '—'

		return {
			key: trainerId || trainerName,
			trainerId,
			trainerName,
			miasto: item.miasto !== '' && item.miasto != null
				? `${item.miasto}`.trim()
				: `${matchedTrainer?.city ?? matchedTrainer?.miasto ?? ''}`.trim(),
			odleglosc_km: item.odleglosc_km !== '' && item.odleglosc_km != null
				? item.odleglosc_km
				: getTrainerDistanceKm(matchedTrainer),
			stawka_godzinowa: item.stawka_godzinowa !== '' && item.stawka_godzinowa != null
				? item.stawka_godzinowa
				: normalizeHourlyRate(matchedTrainer?.defaultHourlyRateGross ?? matchedTrainer?.stawka_godzinowa),
			stawka_za_kilometr: normalizeRatePerKm(item.stawka_za_kilometr),
			ilosc_godzin: item.ilosc_godzin !== '' && item.ilosc_godzin != null
				? item.ilosc_godzin
				: '',
			do_wyplaty: item.do_wyplaty ?? '',
			rozliczono: normalizeRozliczono(item.rozliczono),
			inne_koszty: normalizeTrainerExtraCosts(item.inne_koszty),
		}
	}).sort((left, right) => `${left.trainerName || ''}`.localeCompare(`${right.trainerName || ''}`, 'pl'))
}

export const enrichAssignedTrainerRows = (rows, training) => (
	Array.isArray(rows) ? rows : []
).map(row => {
	const nextRow = {
		...row,
		inne_koszty: normalizeTrainerExtraCosts(row.inne_koszty),
	}

	if (row.do_wyplaty !== '' && row.do_wyplaty != null) {
		return nextRow
	}

	return applyRecalculatedDoWyplaty(nextRow, training)
})

export const buildTrainingTrainerUpdatePayload = (training, trainerRows) => ({
	nazwa: training?.nazwa || training?.trainingName || '',
	placowkaId: training?.placowkaId || null,
	placowkaName: training?.placowkaName || '—',
	// For trainer assignment update we only send fields the backend accepts
	// (it rejects location/time/remarks/travelPlans/proof files, etc).
	certificates: Array.isArray(training?.certificates) ? training.certificates : [],
	assignedTrainers: trainerRows.map(row => toAssignedTrainerApiItem(row, training)),
	data_rozpoczecia: training?.data_rozpoczecia || training?.trainingDate || '',
	data_zakonczenia: training?.data_zakonczenia || training?.trainingEndDate || training?.data_rozpoczecia || '',
	status: training?.status || 'new',
	godzina_rozpoczecia: training?.godzina_rozpoczecia || training?.startTime || '',
	godzina_zakonczenia: training?.godzina_zakonczenia || training?.endTime || '',
	uwagi: training?.uwagi || training?.remarks || '',
	g_placeid: training?.g_placeid || '',
	g_lat: training?.g_lat ?? '',
	g_long: training?.g_long ?? '',
	adres: training?.adres || training?.ulica || '',
	miasto: training?.miasto || '',
	kod_pocztowy: training?.kod_pocztowy || '',
})
